import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { AuthService } from '../services/auth';

interface AuthContextType {
  user: User | null;
  profile: any | null;
  loading: boolean;
  signUp: (data: any) => Promise<any>;
  signIn: (data: any) => Promise<any>;
  signOut: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial user
    console.log('AuthContext: Initial user check starting...');
    AuthService.getCurrentUser().then(async (user) => {
      console.log('AuthContext: Initial user check result:', user?.id);
      setUser(user);
      if (user) {
        try {
          console.log('AuthContext: Loading initial profile for user:', user.id);
          const { profile, error } = await AuthService.getUserProfile(user.id);
          if (error) {
            console.error('AuthContext: Failed to load initial profile:', error);
            setProfile(null);
          } else {
            console.log('AuthContext: Initial profile loaded successfully:', profile);
            setProfile(profile);
          }
        } catch (error) {
          console.error('AuthContext: Error loading initial profile:', error);
          setProfile(null);
        }
      }
      console.log('AuthContext: Setting loading to false');
      setLoading(false);
    }).catch(error => {
      console.error('AuthContext: Error getting current user:', error);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = AuthService.onAuthStateChange(async (user) => {
      console.log('AuthContext: Auth state changed, user:', user?.id);
      setUser(user);
      if (user) {
        try {
          console.log('AuthContext: Loading profile for user:', user.id);
          const { profile, error } = await AuthService.getUserProfile(user.id);
          if (error) {
            console.error('Failed to load profile:', error);
            setProfile(null);
          } else {
            console.log('AuthContext: Profile loaded successfully:', profile);
            setProfile(profile);
          }
        } catch (error) {
          console.error('Error loading profile:', error);
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (data: any) => {
    const result = await AuthService.signUp(data);
    return result;
  };

  const signIn = async (data: any) => {
    const result = await AuthService.signIn(data);
    return result;
  };

  const signOut = async () => {
    const result = await AuthService.signOut();
    return result;
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      signUp,
      signIn,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}